import { SitePhoto, type SiteReportDraft } from '@epc/contracts';
import { ApiError } from '../../../lib/api';
import { scrubMediaLocation } from './media-scrub';
import { confirmSitePhotoReady, recordSitePhotoProgress } from './photos';
import type { OfflineScope, SiteOfflineStore } from './store';

/** Длинная сторона снимка после сжатия на телефоне (ПР-03): на площадке фото уходит по слабой связи. */
export const PHOTO_MAX_SIDE = 1600;
export const PHOTO_QUALITY = 0.8;
/** Больше исходного файла не принимаем: телефон обычно отдаёт 3–12 МБ, это уже много для обработки в браузере. */
export const PHOTO_MAX_SOURCE = 40 * 1024 * 1024;

export interface PreparedPhoto {
  blob: Blob;
  sha256: string;
  size: number;
  capturedAt: string | null;
}

export type PhotoSource = 'camera' | 'gallery';

/** Сжатие снимка; в тестах подменяется. Холст не переносит метаданные EXIF, поэтому место съёмки не уходит с фото. */
export type PhotoCompressor = (file: Blob) => Promise<Blob>;

export const compressInBrowser: PhotoCompressor = async (file) => {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Браузер не смог обработать снимок');
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY),
    );
    if (!blob) throw new Error('Не удалось сжать снимок');
    return blob;
  } finally {
    bitmap.close();
  }
};

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function preparePhoto(
  file: File,
  source: PhotoSource,
  now: Date,
  compress: PhotoCompressor = compressInBrowser,
): Promise<PreparedPhoto> {
  if (!file.type.startsWith('image/')) throw new Error('Можно прикладывать только фотографии');
  if (file.size > PHOTO_MAX_SOURCE) throw new Error('Фото больше 40 МБ. Снимите ещё раз с меньшим размером');
  // Место съёмки с файлом не уходит (МП-07): холст его не переносит, а остатки EXIF/XMP убираются явно
  const blob = await scrubMediaLocation(await compress(file));
  if (!blob.size) throw new Error('Снимок пустой');
  // Камера: время съёмки — сейчас. Галерея: время из файла, если телефон его отдал; иначе неизвестно.
  const stamp = source === 'camera' ? now.getTime() : file.lastModified || 0;
  return {
    blob,
    sha256: await sha256Hex(blob),
    size: blob.size,
    capturedAt: stamp > 0 ? new Date(stamp).toISOString() : null,
  };
}

/** Описание фото/видео в черновике: файл ещё не загружен (fileId = null), загрузка пойдёт при появлении связи. */
export function photoMetadata(
  clientPhotoId: string,
  lineId: string,
  source: PhotoSource,
  prepared: PreparedPhoto & { mimeType?: string | null; kind?: 'photo' | 'video' },
  caption = '',
  geo: {
    locationConsent?: boolean;
    location?: SiteReportDraft['photos'][number]['location'];
  } = {},
): SiteReportDraft['photos'][number] {
  const kind = prepared.kind ?? 'photo';
  return SitePhoto.parse({
    clientPhotoId,
    lineId,
    fileId: null,
    size: prepared.size,
    sha256: prepared.sha256,
    caption,
    capturedAt: prepared.capturedAt,
    source,
    kind,
    mimeType: kind === 'video' ? (prepared.mimeType ?? null) : null,
    locationConsent: geo.locationConsent ?? false,
    location: geo.locationConsent ? (geo.location ?? null) : null,
  });
}

/** Идентификаторы файлов уже загруженных фото переносятся в черновик, чтобы правка текста не стёрла их. */
export function withUploadedFileIds(
  draft: SiteReportDraft,
  local: ReadonlyArray<{ clientPhotoId: string; fileId: string | null }>,
): SiteReportDraft {
  const ready = new Map(local.filter((p) => p.fileId).map((p) => [p.clientPhotoId, p.fileId]));
  if (!draft.photos.some((p) => !p.fileId && ready.has(p.clientPhotoId))) return draft;
  return {
    ...draft,
    photos: draft.photos.map((p) =>
      !p.fileId && ready.has(p.clientPhotoId) ? { ...p, fileId: ready.get(p.clientPhotoId)! } : p,
    ),
  };
}

export const photosPending = (
  draft: Pick<SiteReportDraft, 'photos'>,
  local: ReadonlyArray<{ clientPhotoId: string; fileId: string | null }> = [],
) => withUploadedFileIds(draft as SiteReportDraft, local).photos.filter((p) => !p.fileId).length;

/** Загрузка одного файла в общий файловый слой. Возвращает идентификатор файла. */
export interface PhotoUploader {
  upload(blob: Blob, fileName: string): Promise<{ id: string }>;
  /**
   * Свой уже загруженный файл с тем же содержимым (`GET /site/photo-files/lookup`): если ответ на прошлую
   * загрузку не дошёл, файл берётся с сервера, а не отправляется второй раз.
   */
  findExisting?(sha256: string, size: number): Promise<string | null>;
}

/** Расширение имени при выгрузке: по kind/MIME, иначе .jpg. */
export function mediaFileExt(
  kind: SiteReportDraft['photos'][number]['kind'] | undefined,
  mimeType: string | null | undefined,
): string {
  if (kind === 'video') {
    const mime = (mimeType ?? '').toLowerCase();
    if (mime.includes('webm')) return '.webm';
    if (mime.includes('quicktime') || mime.includes('mov')) return '.mov';
    return '.mp4';
  }
  return '.jpg';
}

export type PhotoSyncResult = {
  sent: number;
  waiting: number;
  stopped: 'offline' | 'auth' | 'refused' | null;
};

/**
 * Отправка накопленных фото отчёта: по одному, каждое один раз. Фото, которое сервер принял, но ответ не дошёл,
 * при повторе сначала ищется на сервере по содержимому (свой файл с тем же SHA-256 и размером) — второй файл не
 * создаётся, и в отчёт фото попадает один раз.
 */
export async function syncSitePhotos(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  uploader: PhotoUploader,
  now: number,
): Promise<PhotoSyncResult> {
  const row = await store.read(scope, clientId);
  if (!row) return { sent: 0, waiting: 0, stopped: null };
  const wanted = new Set((row.draft as SiteReportDraft | null)?.photos?.map((p) => p?.clientPhotoId) ?? []);
  // Фото, убранные из отчёта, с устройства не отправляются и не хранятся
  if (row.photos.some((p) => !wanted.has(p.clientPhotoId)))
    await store.mutate(scope, clientId, (current) => {
      if (!current) throw new Error('Черновик не найден');
      current.photos = current.photos.filter((p) => wanted.has(p.clientPhotoId));
      return { row: current, result: undefined };
    });
  let sent = 0;
  let stopped: PhotoSyncResult['stopped'] = null;
  const draftPhotos = (row.draft as SiteReportDraft | null)?.photos ?? [];
  const queue = row.photos.filter((p) => wanted.has(p.clientPhotoId) && p.state !== 'sent' && !p.fileId);
  for (const photo of queue) {
    const uploadId = photo.uploadId ?? crypto.randomUUID();
    try {
      if (!photo.uploadId)
        await recordSitePhotoProgress(store, scope, clientId, photo.clientPhotoId, { uploadId, parts: {} });
      const meta = draftPhotos.find((p) => p.clientPhotoId === photo.clientPhotoId);
      const fileName = `${photo.clientPhotoId}${mediaFileExt(meta?.kind, meta?.mimeType)}`;
      // Повтор после обрыва: сервер уже мог принять файл — тогда второй не создаётся (ПР-03)
      const existing =
        photo.uploadId && uploader.findExisting
          ? await uploader.findExisting(photo.sha256, photo.size)
          : null;
      const file = existing ? { id: existing } : await uploader.upload(photo.blob, fileName);
      await confirmSitePhotoReady(
        store,
        scope,
        clientId,
        {
          clientPhotoId: photo.clientPhotoId,
          uploadId,
          fileId: file.id,
          sha256: photo.sha256,
          size: photo.size,
          state: 'ready',
        },
        now,
      );
      sent += 1;
    } catch (error) {
      if (error instanceof ApiError) {
        // Вход истёк, нет права или вошёл другой человек — ждём входа владельца, файл не «отказан»
        if (error.status === 401 || error.status === 403 || error.code === 'session_context_changed')
          stopped = 'auth';
        else if (error.status === 0 || error.status >= 500 || error.status === 429) stopped = 'offline';
        else stopped = 'refused';
      } else stopped = 'offline';
      break;
    }
  }
  const after = await store.read(scope, clientId);
  const waiting = after ? photosPending(after.draft as SiteReportDraft, after.photos) : 0;
  return { sent, waiting, stopped };
}
